import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getStaticPageMeta } from '@/seo/meta';
import { getPortfolios } from '@/api/portfolio';
import { localizedTitle } from '@/i18n/localize';
import type { PortfolioItem } from '@/types';

const CATEGORY_ORDER = [
  'Luxury Co-living Development',
  'Luxury Single Dwelling',
  'Contemporary Duplex',
  'Luxury Duplex',
  'Childcare Centre',
  'Industrial Building',
];

export default function Portfolio() {
  const { t, i18n } = useTranslation();
  const [portfolios, setPortfolios] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageMeta(getStaticPageMeta('/portfolio'));

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;

    getPortfolios()
      .then(data => {
        if (cancelled) return;
        setPortfolios(data);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load portfolios:', err);
        setError(err instanceof Error ? err.message : 'Failed to load projects');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sortedPortfolios = useMemo(() => {
    return [...portfolios].sort((a, b) => {
      const aCat = a.categories[0]?.nameEn || '';
      const bCat = b.categories[0]?.nameEn || '';
      const aIndex = CATEGORY_ORDER.indexOf(aCat);
      const bIndex = CATEGORY_ORDER.indexOf(bCat);
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.titleEn.localeCompare(b.titleEn);
    });
  }, [portfolios]);

  return (
    <>
      <div className="page-header parallaxie">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="page-header-box">
                <h1>{t('portfolio.pageTitle')}</h1>
                <nav>
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/">{t('common.home')}</a></li>
                    <li className="breadcrumb-item active">{t('portfolio.breadcrumb')}</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="our-project">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              {loading && <p>{t('portfolio.loading')}</p>}
              {error && <p className="text-danger" style={{ color: '#dc3545', marginBottom: '20px' }}>{error}</p>}
              {!loading && !error && sortedPortfolios.length === 0 && <p>{t('portfolio.noProjects')}</p>}
              {!loading && !error && sortedPortfolios.length > 0 && (
                <div className="row project-item-boxes">
                  {sortedPortfolios.map(item => (
                    <div key={item.id} className="col-lg-4 col-md-6 project-item-box">
                      <div className="project-item">
                        <div className="project-image">
                          <div className="project-featured-image">
                            <img src={item.image} alt={item.title} loading="lazy" />
                          </div>
                          <div className="project-btn">
                            <Link to={`/portfolio/${item.slug}`}><img src="/images/arrow-white.svg" alt="View" /></Link>
                          </div>
                        </div>
                        <div className="project-content">
                          <h3 style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{item.categories.map(c => c.nameEn).join(', ')}</h3>
                          <h2><a href={`/portfolio/${item.slug}`} style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>{localizedTitle(item, i18n.language)}</a></h2>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
